import styles from "./ContentLoader.module.css";

/**
 * Generic shimmer "skeleton" block. Compose these to build loaders that
 * roughly match the shape of the content they stand in for, so the page
 * doesn't jump around once real data arrives.
 */
export const SkeletonBlock = ({ width = "100%", height = 16, radius = 6, style = {} }) => (
    <span
        className={styles.SkeletonBlock}
        style={{ width, height, borderRadius: radius, ...style }}
    />
);

const HeaderLoader = () => (
    <div className={styles.HeaderLoader}>
        <SkeletonBlock width={190} height={24} radius={7} />
        <SkeletonBlock width={110} height={12} radius={4} />
    </div>
);

const ToolbarLoader = () => (
    <div className={styles.ToolbarLoader}>
        <SkeletonBlock width="min(320px, 60%)" height={42} radius={10} />
        <div className={styles.ToolbarLoaderActions}>
            <SkeletonBlock width={128} height={38} radius={9} />
            <SkeletonBlock width={128} height={38} radius={9} />
        </div>
    </div>
);

const LegendLoader = () => (
    <div className={styles.LegendLoader}>
        {[92, 150, 140, 110].map((w, i) => (
            <SkeletonBlock key={i} width={w} height={26} radius={999} />
        ))}
    </div>
);

/**
 * Desktop table skeleton — column widths are staggered so it reads as
 * "text of varying length" rather than a uniform gray brick.
 */
const DesktopTableSkeleton = ({ rows, columns }) => (
    <div className={styles.DesktopTableLoaderWrap}>
        <div className={styles.TableLoaderCard}>
            <div className={`${styles.TableLoaderRow} ${styles.TableLoaderHeadRow}`}>
                {Array.from({ length: columns }).map((_, i) => (
                    <SkeletonBlock key={`head-${i}`} height={10} width={i === 0 ? "65%" : "50%"} radius={4} />
                ))}
            </div>
            {Array.from({ length: rows }).map((_, r) => (
                <div className={styles.TableLoaderRow} key={`row-${r}`}>
                    {Array.from({ length: columns }).map((_, c) => (
                        <SkeletonBlock
                            key={`cell-${r}-${c}`}
                            height={13}
                            width={c === 0 ? "78%" : `${50 + ((r + c) % 4) * 10}%`}
                            radius={4}
                        />
                    ))}
                </div>
            ))}
        </div>
    </div>
);

/**
 * Mobile card skeleton — mirrors MobileCard's structure 1:1 (ref + status
 * chip, dashed route line, 2x2 meta grid, two action buttons) so the
 * loading -> loaded swap doesn't shift layout on a phone.
 */
const MobileCardSkeleton = ({ items }) => (
    <div className={styles.MobileCardListLoaderWrap}>
        {Array.from({ length: items }).map((_, i) => (
            <div className={styles.MobileCardLoader} key={i}>
                <div className={styles.MobileCardLoaderTop}>
                    <div>
                        <SkeletonBlock width={90} height={15} radius={5} />
                        <div style={{ marginTop: 6 }}>
                            <SkeletonBlock width={120} height={11} radius={4} />
                        </div>
                    </div>
                    <SkeletonBlock width={72} height={22} radius={999} />
                </div>

                <div className={styles.MobileCardLoaderRoute}>
                    <SkeletonBlock width="38%" height={12} radius={4} />
                    <SkeletonBlock width={14} height={14} radius={4} />
                    <SkeletonBlock width="38%" height={12} radius={4} />
                </div>

                <div className={styles.MobileCardLoaderMeta}>
                    {Array.from({ length: 4 }).map((_, m) => (
                        <div key={m}>
                            <SkeletonBlock width={54} height={9} radius={3} style={{ marginBottom: 5 }} />
                            <SkeletonBlock width="80%" height={12} radius={4} />
                        </div>
                    ))}
                </div>

                <div className={styles.MobileCardLoaderActions}>
                    <SkeletonBlock height={34} radius={9} />
                    <SkeletonBlock height={34} radius={9} />
                </div>
            </div>
        ))}
    </div>
);

/**
 * Full-page loader for the shipping list. Renders a desktop table skeleton
 * and a mobile card skeleton together; CSS shows/hides whichever matches
 * the current viewport at the same breakpoint the real content uses.
 */
export const TableLoader = ({ rows = 6, columns = 8, cards = 4 }) => (
    <div role="status" aria-label="Loading shipping list">
        <HeaderLoader />
        <ToolbarLoader />
        <LegendLoader />
        <DesktopTableSkeleton rows={rows} columns={columns} />
        <MobileCardSkeleton items={cards} />
        <span className={styles.VisuallyHidden}>Loading…</span>
    </div>
);

export default TableLoader;